// Országbeállítások (slice 4.4, on top of 0.1's Küldő-fiókok): per country, the
// sending account — whose name the letters go out under, what the footer says,
// which domains the links point at, which pool and SES account carry it — set
// the way Klaviyo's account settings are set. An admin edits; a marketer reads.
//
// The API is the authority. Every value is judged by the gate the campaign
// runner refuses a start on (emc_db.services.account_readiness), plus the
// settings rules (sending domain verified, tracking domain served, active only
// when ready); a refusal comes back on the fields it names. Every save is one
// event — who, when, old → new — and the history is shown beside the form. The
// footer preview is rendered by the sending engine itself, from the values in
// the form, before they are saved.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CircleAlertIcon, CircleCheckIcon, CircleXIcon, LockIcon } from "lucide-react";
import {
  createSendingAccount,
  fetchSendingAccountEvents,
  fetchSendingAccounts,
  previewFooter,
  updateSendingAccount,
} from "@/lib/api";
import type {
  FindingStatus,
  FooterPreview,
  ReadinessFinding,
  SendingAccount,
  SendingAccountEvent,
  SendingAccountsView,
  SettingsCountry,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { Country } from "@/components/emc/Flag";
import { cn } from "@/lib/utils";
import { messageOf } from "@/pages/campaigns/model";
import { useIsAdmin } from "@/pages/warmup/actions";
import {
  FIELDS,
  ROUTING_FIELDS,
  SECTIONS,
  blankForm,
  changeLine,
  changesOf,
  checkForm,
  createValues,
  eventTitle,
  formOf,
  refusalOf,
  valuesOf,
} from "@/pages/settings/model";
import type { FieldSpec, FormValues, Refusal } from "@/pages/settings/model";

const FINDING_LOOK: Record<FindingStatus, { icon: typeof CircleCheckIcon; className: string; word: string }> = {
  ok: { icon: CircleCheckIcon, className: "text-success", word: "rendben" },
  fail: { icon: CircleXIcon, className: "text-destructive", word: "hiba" },
  warn: { icon: CircleAlertIcon, className: "text-warning", word: "figyelmeztetés" },
};

// How long the form waits after the last keystroke before it asks for a new
// footer: a render goes through MJML, so not on every key.
const PREVIEW_DELAY_MS = 600;

function FindingRows({ findings }: { findings: ReadinessFinding[] }) {
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {findings.map((finding) => {
        // A status this page does not know is shown grey under its wire name,
        // the way status.ts treats an unknown state — never a blank card.
        const look = FINDING_LOOK[finding.status] ?? {
          icon: CircleAlertIcon,
          className: "text-muted-foreground",
          word: finding.status,
        };
        const Icon = look.icon;
        return (
          <li
            key={finding.key}
            className="flex items-start gap-2 py-1"
            data-finding={finding.key}
            data-status={finding.status}
          >
            <Icon className={cn("mt-0.5 size-4 shrink-0", look.className)} aria-hidden="true" />
            <span className={cn("min-w-0 break-words", finding.status === "ok" && "text-muted-foreground")}>
              <span className="sr-only">{look.word}: </span>
              {finding.message}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The problems in full; what passed, folded — twelve green lines per country
 * would bury the one red one. */
function Findings({ findings }: { findings: ReadinessFinding[] }) {
  const problems = findings.filter((finding) => finding.status !== "ok");
  const fine = findings.filter((finding) => finding.status === "ok");
  return (
    <div className="flex flex-col gap-2">
      {problems.length > 0 && <FindingRows findings={problems} />}
      {fine.length > 0 && (
        <details>
          <summary className="w-fit cursor-pointer text-muted-foreground">{fine.length} ellenőrzés rendben</summary>
          <div className="pt-1">
            <FindingRows findings={fine} />
          </div>
        </details>
      )}
    </div>
  );
}

function stateOf(account: SendingAccount | undefined): string {
  if (account === undefined) return "nincs fiók";
  if (!account.readiness.ready) return "hiányos";
  return account.is_active ? "kész" : "kész, inaktív";
}

// --- one field ------------------------------------------------------------------

interface FieldProps {
  spec: FieldSpec;
  value: string | number | boolean;
  error: string | undefined;
  disabled: boolean;
  note: string | null;
  view: SendingAccountsView;
  onChange: (value: string) => void;
}

function SettingField({ spec, value, error, disabled, note, view, onChange }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const text = String(value ?? "");
  let control;
  if (spec.kind === "ses" || spec.kind === "pool") {
    const options =
      spec.kind === "ses"
        ? view.ses_accounts.map((letter) => ({ value: letter, label: letter }))
        : view.pools.map((pool) => ({
            value: pool.code,
            label: `${pool.code} · ${pool.name} (SES ${pool.ses_account})`,
          }));
    control = (
      <NativeSelect
        id={id}
        className="w-full"
        value={text}
        disabled={disabled}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={hintId}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">— válassz —</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </NativeSelect>
    );
  } else {
    const numeric = spec.kind === "hour" || spec.kind === "count";
    control = (
      <Input
        id={id}
        value={text}
        disabled={disabled}
        inputMode={numeric ? "numeric" : spec.kind === "email" ? "email" : spec.kind === "url" ? "url" : undefined}
        placeholder={spec.placeholder}
        maxLength={spec.max}
        className={cn(spec.kind !== "text" && "data-mono")}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={hintId}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <div data-field={spec.name} className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>
        {spec.label}
        {spec.required && <span className="text-muted-foreground"> *</span>}
      </Label>
      {control}
      <div id={hintId} className="flex flex-col gap-0.5 text-label">
        {error !== undefined && (
          <span className="text-destructive" role="alert">
            {error}
          </span>
        )}
        {note !== null && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <LockIcon className="size-3.5 shrink-0" aria-hidden="true" />
            {note}
          </span>
        )}
        {error === undefined && spec.hint && <span className="text-muted-foreground">{spec.hint}</span>}
      </div>
    </div>
  );
}

// --- the footer preview -----------------------------------------------------------

function FooterPreviewCard({ country, form }: { country: string; form: FormValues }) {
  const [preview, setPreview] = useState<FooterPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const latest = useRef(0);
  const values = useMemo(() => valuesOf(form), [form]);

  useEffect(() => {
    const ask = ++latest.current;
    const timer = window.setTimeout(() => {
      previewFooter(country, values)
        .then((answer) => {
          if (ask !== latest.current) return;
          setPreview(answer);
          setProblem(null);
        })
        .catch((raised: unknown) => {
          if (ask !== latest.current) return;
          setPreview(null);
          setProblem(messageOf(raised));
        });
    }, PREVIEW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [country, values]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>A lábléc, ahogy a címzett látja</CardTitle>
        <CardDescription>
          A küldő motor rajzolja meg a mezők értékéből, mentés előtt is. Minden levél alján ez áll; a leiratkozó
          link itt egy minta-címzetté, és nem iratkoztat le senkit.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {problem !== null && <Callout tone="warn">{problem}</Callout>}
        {preview === null && problem === null && <LoadingState lines={3} label="A lábléc rajzolása…" />}
        {preview !== null && (
          <>
            {/* No scripts, no same origin: the letter is shown, never run. */}
            <iframe
              title="A lábléc előnézete"
              sandbox=""
              srcDoc={preview.html}
              className="h-48 w-full rounded-inner border border-separator bg-card"
            />
            <details>
              <summary className="w-fit cursor-pointer text-muted-foreground">Szöveges változat</summary>
              <pre className="data-mono mt-2 whitespace-pre-wrap break-words text-label">{preview.text}</pre>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- the history ------------------------------------------------------------------

function History({ account }: { account: SendingAccount }) {
  const [events, setEvents] = useState<SendingAccountEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { id, updated_at: updatedAt } = account;

  const reload = useCallback(() => {
    setError(null);
    fetchSendingAccountEvents(id)
      .then(setEvents)
      .catch((raised: unknown) => setError(messageOf(raised)));
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload, updatedAt]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Előzmények</CardTitle>
        <CardDescription>Minden mentés: ki, mikor, mit mire változtatott.</CardDescription>
      </CardHeader>
      <CardContent>
        {error !== null && <ErrorState message={error} onRetry={reload} />}
        {events === null && error === null && <LoadingState lines={3} label="Az előzmények betöltése…" />}
        {events !== null && events.length === 0 && (
          <p className="text-muted-foreground">
            Nincs bejegyzés: a fiók a beállítások oldala előtt, migrációval érkezett.
          </p>
        )}
        {events !== null && events.length > 0 && (
          <ol className="m-0 flex list-none flex-col gap-3 p-0">
            {events.map((event, index) => (
              <li
                key={`${event.occurred_at}-${index}`}
                className="flex flex-col gap-1 border-b border-separator pb-3 last:border-0"
              >
                <span>
                  <span className="font-semibold">{eventTitle(event)}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {event.actor} · <span className="data-mono">{minute(event.occurred_at)}</span>
                  </span>
                </span>
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-label">
                  {event.changes
                    .filter((change) => event.action !== "account.created" || (change.new !== "" && change.new !== null))
                    .map((change) => (
                      <li key={change.field} className="break-words">
                        {changeLine(change)}
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

// --- one country --------------------------------------------------------------------

interface PanelProps {
  view: SendingAccountsView;
  country: SettingsCountry;
  account: SendingAccount | undefined;
  isAdmin: boolean;
  onSaved: () => void;
}

function CountryPanel({ view, country, account, isAdmin, onSaved }: PanelProps) {
  const initial = useMemo(() => (account !== undefined ? formOf(account) : blankForm(country)), [account, country]);
  const [form, setForm] = useState<FormValues>(initial);
  const [server, setServer] = useState<Refusal | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial);
    setServer(null);
    setTried(false);
  }, [initial]);

  const readOnly = !isAdmin;
  const running = account?.running_campaigns ?? 0;
  const { changes, expected } = changesOf(initial, form);
  const changed = Object.keys(changes);
  const active = Boolean(form.is_active);
  const local = checkForm(
    form,
    {
      verified: view.verified_sending_domains,
      tracking: view.tracking_domains,
      sesAccounts: view.ses_accounts,
      pools: view.pools.map((pool) => pool.code),
    },
    active,
  );
  // A sentence under a field once it has been touched, or once a save was
  // tried; the server's refusal wins over the quick check.
  const errorFor = (name: string): string | undefined =>
    server?.fields[name] ?? (tried || name in changes ? local[name] : undefined);

  const chosenPool = view.pools.find((pool) => pool.code === String(form.ip_pool_code ?? "").toUpperCase());
  const sesMismatch =
    chosenPool !== undefined &&
    String(form.ses_account ?? "") !== "" &&
    chosenPool.ses_account !== String(form.ses_account).toUpperCase();

  function set(name: string, value: string | boolean) {
    setForm((current) => ({ ...current, [name]: value }));
    setServer((current) => {
      if (current === null || !(name in current.fields)) return current;
      const fields = { ...current.fields };
      delete fields[name];
      return { ...current, fields };
    });
  }

  function discard() {
    setForm(initial);
    setServer(null);
    setTried(false);
  }

  async function save() {
    setTried(true);
    if (Object.keys(local).length > 0) {
      setServer({ message: "Javítsd a jelölt mezőket, aztán mentsd újra.", fields: {} });
      return;
    }
    setSaving(true);
    try {
      if (account === undefined) {
        await createSendingAccount(country.code, createValues(form));
        toast(`${country.code}: a küldő-fiók elkészült.`);
      } else {
        await updateSendingAccount(account.id, changes, expected);
        toast(`${country.code}: mentve (${changed.length} mező).`);
      }
      setServer(null);
      setTried(false);
      onSaved();
    } catch (raised: unknown) {
      setServer(refusalOf(raised));
    } finally {
      setSaving(false);
    }
  }

  if (account === undefined && readOnly) {
    return (
      <EmptyState
        title={`${country.code}: még nincs küldő-fiók.`}
        description="Admin hozhatja létre ezen az oldalon. Amíg nincs, ebben az országban kampány nem indulhat."
      />
    );
  }

  const noteFor = (name: string): string | null => {
    if (ROUTING_FIELDS.includes(name) && running > 0) {
      return "Kampány fut ebből a fiókból: amíg be nem fejeződik, nem módosítható.";
    }
    if (name === "ses_account" && sesMismatch) {
      return `A választott pool a(z) ${chosenPool?.ses_account} SES-fiókon küld; a fiók SES-fiókja is ez kell legyen.`;
    }
    return null;
  };

  return (
    <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,28rem)]">
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Country code={country.code} />
              <span className="font-normal text-muted-foreground">
                {account === undefined ? "új küldő-fiók" : account.label || "küldő-fiók"}
              </span>
            </CardTitle>
            <CardDescription>
              {account === undefined
                ? "Inaktívként félkészen is menthető; aktív csak akkor lehet, ha a kapu késznek ítéli."
                : `Módosítva ${minute(account.updated_at)}.`}
              {running > 0 && ` Ebből a fiókból most ${running} kampány fut.`}
            </CardDescription>
            {account !== undefined && (
              <CardAction>
                <StatusPill domain="accountReadiness" state={account.readiness.ready ? "ready" : "incomplete"} />
              </CardAction>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id={`active-${country.code}`}
                checked={active}
                disabled={readOnly}
                onCheckedChange={(checked) => set("is_active", checked)}
              />
              <Label htmlFor={`active-${country.code}`}>
                {active ? "Aktív: kampány választhatja" : "Inaktív: kampány nem választhatja"}
              </Label>
            </div>
            {account !== undefined && <Findings findings={account.readiness.findings} />}
          </CardContent>
        </Card>

        {SECTIONS.map((section) => (
          <Card key={section.id} data-section={section.id}>
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {section.fields.map((spec) => (
                <SettingField
                  key={spec.name}
                  spec={spec}
                  value={form[spec.name] ?? ""}
                  error={errorFor(spec.name)}
                  disabled={readOnly || (ROUTING_FIELDS.includes(spec.name) && running > 0)}
                  note={noteFor(spec.name)}
                  view={view}
                  onChange={(value) => set(spec.name, value)}
                />
              ))}
            </CardContent>
          </Card>
        ))}

        {!readOnly && (
          <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 rounded-card border border-separator bg-card p-3">
            {server !== null && (
              <Callout tone="warn" className="w-full">
                {server.message}
              </Callout>
            )}
            <span className="text-muted-foreground">
              {account === undefined
                ? "Új fiók: minden mező az első mentéssel kerül be, egy naplóbejegyzésben."
                : changed.length === 0
                  ? "Nincs mentetlen változás."
                  : `${changed.length} mező változott: ${changed
                      .map((name) => FIELDS.find((field) => field.name === name)?.label ?? "Aktív állapot")
                      .join(", ")}.`}
            </span>
            <span className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving || (account !== undefined && changed.length === 0)}
                onClick={discard}
              >
                Elvetés
              </Button>
              <Button
                type="button"
                disabled={saving || (account !== undefined && changed.length === 0)}
                onClick={() => void save()}
              >
                {saving ? "Mentés…" : account === undefined ? "Létrehozás" : "Mentés"}
              </Button>
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 xl:sticky xl:top-4">
        <FooterPreviewCard country={country.code} form={form} />
        {account !== undefined && <History account={account} />}
      </div>
    </div>
  );
}

// --- the page ----------------------------------------------------------------------

export default function SendingAccounts() {
  const [view, setView] = useState<SendingAccountsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const isAdmin = useIsAdmin();

  const reload = useCallback(() => {
    setError(null);
    fetchSendingAccounts()
      .then(setView)
      .catch((problem: unknown) => setError(messageOf(problem)));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const countries = view?.countries ?? [];
  const byCountry = new Map((view?.accounts ?? []).map((account) => [account.country, account]));
  const wanted = (params.get("orszag") ?? "").toUpperCase();
  const selected =
    countries.find((country) => country.code === wanted) ??
    countries.find((country) => byCountry.has(country.code)) ??
    countries[0];
  const gate = view?.deployment ?? null;
  const refused = gate === null || gate.enforced;
  const incomplete = (view?.accounts ?? []).filter((account) => !account.readiness.ready);

  return (
    <>
      <PageHeader
        title="Országbeállítások"
        description={
          <>
            Országonként egy küldő-fiók: ezen a néven és címről megy ki a levél, ez a cégadat, weboldal és
            leiratkozó szöveg áll a láblécben, erre a követő domainre mutat minden link, és ez a pool viszi.
            {isAdmin ? " Minden mentés naplózva." : " Csak admin szerkesztheti."}
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {error !== null && <ErrorState message={error} onRetry={reload} />}
        {view === null && error === null && <LoadingState lines={6} label="A fiókok betöltése…" />}

        {view !== null && (
          <Callout tone="info">
            Ellenőrzött küldő domainek:{" "}
            <span className="data-mono">{view.verified_sending_domains.join(", ") || "egy sincs megadva"}</span> (
            <code>EMC_VERIFIED_SENDING_DOMAINS</code>) · kiszolgált követő domainek:{" "}
            <span className="data-mono">{view.tracking_domains.join(", ") || "egy sincs megadva"}</span> (
            <code>EMC_TRACKING_DOMAINS</code>). Hogy a követő domain feloldható-e, és a <code>/u/</code> a mi
            API-nkhoz ér-e, azt a hálózat tudja — az <code>emc doctor</code> nézi meg.
          </Callout>
        )}

        {view !== null && incomplete.length > 0 && (
          <Callout tone="warn">
            Hiányos: <span className="data-mono">{incomplete.map((account) => account.country).join(", ")}</span>.{" "}
            {refused
              ? "Ezekben az országokban a kampány nem indul el, amíg a fiók nem kész."
              : `Ez a környezet nem küld valódi levelet (EMC_ENV=${gate?.env} · EMC_TRANSPORT=${gate?.transport}), ezért itt a kampány elindul, és a naplója rögzíti, mi hiányzott. Élesben ez megállítaná a kiküldést.`}
          </Callout>
        )}

        {view !== null && selected !== undefined && (
          <Tabs value={selected.code} onValueChange={(code) => setParams({ orszag: code }, { replace: true })}>
            <TabsList aria-label="Országok">
              {countries.map((country) => {
                const account = byCountry.get(country.code);
                return (
                  <TabsTrigger key={country.code} value={country.code} title={stateOf(account)}>
                    <Country code={country.code} />
                    <span
                      role="img"
                      aria-label={stateOf(account)}
                      className={cn(
                        "size-1.5 rounded-pill",
                        account === undefined
                          ? "bg-fill-secondary"
                          : account.readiness.ready
                            ? "bg-success"
                            : "bg-destructive",
                      )}
                    />
                  </TabsTrigger>
                );
              })}
            </TabsList>
            <CountryPanel
              key={selected.code}
              view={view}
              country={selected}
              account={byCountry.get(selected.code)}
              isAdmin={isAdmin}
              onSaved={reload}
            />
          </Tabs>
        )}
      </div>
    </>
  );
}
